/** Revenue — blended ARR detail, MRR series, subscriptions. Sandbox labeled, never blended. */
import { Bars, Chip, Empty, ErrorNote, Panel, Readout, statusTone, useApi } from "../components/ui.js";
import { fmtDate, fmtMonthDay, int, n, usd } from "../lib/format.js";
import type { ArrRow, MrrDay, SubscriptionRow } from "../lib/types.js";

interface AttributionRow {
  channel: string; campaign: string | null; clicks: unknown; installs: unknown;
  installs_identified: unknown; activated_48h: unknown; subscriptions: unknown; mrr_usd: unknown;
}

interface AccountRow {
  domain: string; company_name: string | null; contacts: unknown; contacts_engaged: unknown;
  installs: unknown; active_users_30d: unknown; analyses_30d: unknown;
  subscriptions: unknown; mrr_usd: unknown; expansion_candidate: boolean;
}

export function Revenue() {
  const arr = useApi<{ arr: ArrRow[] }>("/api/revenue/arr");
  const attribution = useApi<{ attribution: AttributionRow[] }>("/api/attribution");
  const accounts = useApi<{ accounts: AccountRow[] }>("/api/accounts");
  const series = useApi<{ series: MrrDay[] }>("/api/revenue/mrr-by-day");
  const subs = useApi<{ subscriptions: SubscriptionRow[] }>("/api/subscriptions");

  const rows = arr.data?.arr ?? [];
  const prod = rows.filter((r) => r.environment === "production");
  const sandbox = rows.filter((r) => r.environment === "sandbox");
  const prodSeries = (series.data?.series ?? []).filter((d) => d.environment === "production" || d.environment === null);

  return (
    <>
      <div className="page-head"><h1>Revenue</h1><span className="stamp">app track + accelerator track</span></div>

      <div className="grid cols-3" style={{ marginBottom: 14 }}>
        <Panel title="Production MRR" delay={0}>
          <Readout hero label="apple + stripe" value={usd(prod.reduce((s, r) => s + n(r.mrr_usd), 0))}
                   note={prod.length === 0 ? "no production subscriptions yet — billing fully wired + sandbox-proven; awaiting App Store approval + first production purchase" : undefined} />
        </Panel>
        <Panel title="Production ARR" delay={60}>
          <Readout hero label="annualized" value={usd(prod.reduce((s, r) => s + n(r.arr_usd), 0), { compact: true })} />
        </Panel>
        <Panel title="Sandbox / TestFlight" delay={120}>
          <Readout hero label="labeled, never blended" value={int(sandbox.reduce((s, r) => s + n(r.subscriptions), 0))} tone="dim"
                   note="test subscriptions · pending app integration" />
        </Panel>
      </div>

      <div className="grid cols-2" style={{ marginBottom: 14 }}>
        <Panel title="MRR by day · trailing 90 (production)" delay={160}>
          {series.error ? <ErrorNote error={series.error} /> : (
            <Bars values={prodSeries.map((d) => n(d.mrr_usd))} labels={prodSeries.map((d) => fmtMonthDay(d.day))} />
          )}
        </Panel>
        <Panel title="By tier" delay={200}>
          {rows.length === 0 ? <Empty>no subscription rows</Empty> : (
            <table className="data">
              <thead><tr><th>env</th><th>platform</th><th>tier</th><th>subs</th><th>paying</th><th>mrr</th></tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td>{r.environment === "production" ? <Chip tone="green">prod</Chip> : <Chip tone="amber">sandbox</Chip>}</td>
                    <td className="dim">{r.platform}</td>
                    <td className="mono">{r.tier}</td>
                    <td className="num">{int(r.subscriptions)}</td>
                    <td className="num">{int(r.paying)}</td>
                    <td className="num">{usd(r.mrr_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      </div>

      <Panel title="Subscriptions" delay={240}>
        {(subs.data?.subscriptions ?? []).length === 0 ? <Empty>none yet</Empty> : (
          <table className="data">
            <thead><tr><th>contact</th><th>env</th><th>tier</th><th>status</th><th>price</th><th>renews</th></tr></thead>
            <tbody>
              {(subs.data?.subscriptions ?? []).map((s) => (
                <tr key={s.id}>
                  <td>{s.contact_email ?? <span className="faint">unlinked</span>}</td>
                  <td className="dim">{s.environment}</td>
                  <td className="mono">{s.tier}</td>
                  <td><Chip tone={statusTone(s.status)}>{s.status}</Chip></td>
                  <td className="num">{usd(s.price_usd)}</td>
                  <td className="num dim">{fmtDate(s.current_period_end)}{s.will_renew ? "" : " · ends"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Panel title="Attribution by channel — clicks → installs → activations → MRR" delay={280}>
        {(attribution.data?.attribution ?? []).length === 0 ? (
          <Empty>no attributed traffic yet — channel links: turboproc.com/go?ch=linkedin|youtube|web&c=campaign (builder in Controls)</Empty>
        ) : (
          <table className="data">
            <thead><tr><th>channel</th><th>campaign</th><th>clicks</th><th>attr. installs</th><th>identified</th><th>activated ≤48h</th><th>subs</th><th>mrr</th></tr></thead>
            <tbody>
              {(attribution.data?.attribution ?? []).map((a, i) => (
                <tr key={i}>
                  <td><Chip tone="blue">{a.channel}</Chip></td>
                  <td className="dim">{a.campaign ?? "—"}</td>
                  <td className="num">{int(a.clicks)}</td>
                  <td className="num">{int(a.installs)}</td>
                  <td className="num dim">{int(a.installs_identified)}</td>
                  <td className="num">{int(a.activated_48h)}</td>
                  <td className="num">{int(a.subscriptions)}</td>
                  <td className="num">{usd(a.mrr_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Panel title="Accounts — seat expansion candidates (internal only)" delay={320}>
        {(accounts.data?.accounts ?? []).length === 0 ? (
          <Empty>no work-domain accounts yet — appears as contacts and installs cluster by company email domain</Empty>
        ) : (
          <table className="data">
            <thead><tr><th>domain</th><th>company</th><th>contacts</th><th>engaged</th><th>installs</th><th>active 30d</th><th>analyses 30d</th><th>subs</th><th>mrr</th><th></th></tr></thead>
            <tbody>
              {(accounts.data?.accounts ?? []).map((a) => (
                <tr key={a.domain}>
                  <td className="mono">{a.domain}</td>
                  <td className="dim">{a.company_name ?? "—"}</td>
                  <td className="num">{int(a.contacts)}</td>
                  <td className="num dim">{int(a.contacts_engaged)}</td>
                  <td className="num">{int(a.installs)}</td>
                  <td className="num">{int(a.active_users_30d)}</td>
                  <td className="num">{int(a.analyses_30d)}</td>
                  <td className="num">{int(a.subscriptions)}</td>
                  <td className="num">{usd(a.mrr_usd)}</td>
                  <td>{a.expansion_candidate ? <Chip tone="amber">expansion</Chip> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="tag" style={{ marginTop: 8 }}>
          motion guardrail: internal scoring surface only — never outbound copy · expansion = installed + 2 or more known colleagues
        </div>
      </Panel>
    </>
  );
}
